import React, { useContext, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { FileCsv, Plus, Pencil, TrashSimple, ArrowBendDownRight, ArrowLeft, FileArchive, MagnifyingGlass, Copy } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../../components/Header';
import { LoadingScreen } from '../../components/LoadingScreen';
import { UpdateDataModal } from '../../components/UpdateDataModal';
import { Button } from '../../components/Button';
import { TextComponent } from '../../components/TextComponent';
import { InputComponent } from '../../components/InputComponent';
import { Project } from '../../types/ProjectType';
import { useTheme } from '../../providers/theme';
import { InfoContainer, InputSearchContainer } from './styles';
import { UserDataContext } from '../../providers/userData';
import { DownloadCsvModal } from '../../components/DownloadCsvModal';
import { DialogAlert } from '../../components/DialogAlert';
import { ContractNameContext } from '../../providers/contractName';

type ProjectListProps = {
  isLoading: boolean;
  projects: Project[];
  onEditProjectData: Project;
  isEditProjectModalOpen: boolean;
  setIsEditProjectModalOpen: (value: boolean) => void;
  onEdit: (project: Project) => void;
  onDuplicate: (project: Project) => void;
  onDelete: (projectRow: Project) => void;
  goToProject: (projectRow: Project) => void;
  fetchCsvFile: (projectRow: Project) => void;
  getProjectsData: () => void;
}

export function ProjectList(
  {
    isLoading, projects, onEditProjectData, isEditProjectModalOpen,
    setIsEditProjectModalOpen, onEdit, onDuplicate, onDelete, goToProject, getProjectsData
  }: ProjectListProps) {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const { theme } = useTheme();
  const { userData } = useContext(UserDataContext);
  const { contractNumber } = useContext(ContractNameContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [openCsvModal, setOpenCsvModal] = useState<boolean>(false);
  const [projectCsv, setProjectCsv] = useState<Project>();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [projectRow, setProjectRow] = useState<Project>();
  const [projectRowName, setProjectRowName] = useState<string>();
  const { t } = useTranslation();


  const filteredAndSortedData = (data: Project[]) => {
    return data
      ?.filter(item =>
        Object.values(item).some(value =>
          typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
      .sort((a, b) => {
        const titleA = a.title.toUpperCase();
        const titleB = b.title.toUpperCase();

        if (titleA < titleB) {
          return -1;
        }
        if (titleA > titleB) {
          return 1;
        }

        return 0;
      });
  };

  const projectCsvData = (project: Project) => {
    setOpenCsvModal(true);
    setProjectCsv(project);
  };

  function handleDeleteProject(project: Project) {
    setDialogOpen(true);
    setProjectRow(project);
    setProjectRowName(project.title);
  }

  function handleDeleteProjectConfirmed() {
    setDialogOpen(false);
    if(projectRow){
      onDelete(projectRow);
    }
  }

  function handleClose() {
    setDialogOpen(false);
  }

  return (
    <>
      {isLoading ? (
        <LoadingScreen isVisible={isLoading} />
      ) : (
        <>
          <Header />

          <InfoContainer>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 30, flex: 1 }} >
              <Tooltip title={t('group-page-view')}>
                <ArrowLeft onClick={() => navigate('/contracts')} size={30} color={theme.colors.main} cursor='pointer' />
              </Tooltip>
              <TextComponent fontWeight='bold'>
                {t('group-list-intro')}
              </TextComponent>

              {userData.admin && (
                <>
                  <Button
                    id='newProject'
                    onClick={() => navigate(`/contracts/${contractId}/project`)}
                    type='button'
                    color={theme.colors.main}
                    sxhover={theme.colors.hoverMain}
                    sxwidth='160px'
                    sxgap='10px'
                  >
                    <Plus size={20} weight='bold' />
                    {t('group-list-button')}
                  </Button>
                  <TextComponent
                    children={ contractNumber }
                    fontWeight='bold'
                  />
                </>
              )}

            </div>
            <InputSearchContainer>
              <div style={{ paddingLeft: 10 }}>
                <MagnifyingGlass color={theme.colors.secondary} size={25} cursor='pointer' />
              </div>
              <InputComponent
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('group-searchbar')}
                style={{ outline: 'none' }}
              />
            </InputSearchContainer>
          </InfoContainer>
          {
            projects === undefined ? (
              <div style={{ height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                <h3>{t('group-no-projects')}</h3>
              </div >
            ) : (
              <TableContainer sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', marginTop: 3, marginBottom: 3 }}>
                <Table sx={{ background: '#F3F3F3', borderRadius: 1, width: '90%' }}>
                  <TableHead sx={{ background: '#D9D9D9', }}>
                    <TableRow>
                      <TableCell>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('group-table-title')}</span>
                      </TableCell>
                      <TableCell>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('group-table-description')}</span>
                      </TableCell>
                      <TableCell>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('group-table-manager')}</span>
                      </TableCell>
                      <TableCell>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('group-table-actions')}</span>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      filteredAndSortedData(projects)?.map(project => (
                        <React.Fragment key={project.id}>
                          <TableRow>
                            <TableCell>
                              <span>{project.title}</span>
                            </TableCell>
                            <TableCell>
                              <span>
                                {project.description}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span>
                                {project.owner_id}
                              </span>
                            </TableCell>
                            <TableCell sx={{ display: 'flex', gap: 2 }}>
                              <Tooltip title={t('group-table-action-download')}>
                                <FileCsv onClick={() => projectCsvData(project)} size={30} color={theme.colors.secondary} cursor='pointer' />
                              </Tooltip>

                              {userData.admin && (
                                <>
                                  <Tooltip title={t('group-table-action-duplicate')}>
                                    <Copy onClick={() => onDuplicate(project)} size={30} color={theme.colors.purple} cursor='pointer' />
                                  </Tooltip>

                                  <Tooltip title={t('group-table-action-edit')}>
                                    <Pencil onClick={() => onEdit(project)} size={30} color={theme.colors.main} cursor='pointer' />
                                  </Tooltip>

                                  <Tooltip title={t('group-table-action-delete')}>
                                    <TrashSimple onClick={() => handleDeleteProject(project)} size={30} color={theme.colors.red} cursor='pointer' />
                                  </Tooltip>
                                </>
                              )}

                              <Tooltip title={t('group-table-action-answers')}>
                                <FileArchive onClick={() => navigate(`/contracts/${contractId}/project/${project.id}/answers`)} size={30} color={theme.colors.blue} cursor='pointer' />
                              </Tooltip>

                              <Tooltip title={t('group-table-action-access')}>
                                <ArrowBendDownRight onClick={() => goToProject(project)} size={30} color={theme.colors.green} cursor='pointer' />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }

          {
            isEditProjectModalOpen && (
              <UpdateDataModal
                getProjectsData={getProjectsData}
                getContractsData={() => null}
                onEditProject={true}
                onEditContract={false}
                dataProject={onEditProjectData}
                isOpen={isEditProjectModalOpen}
                toggle={() => setIsEditProjectModalOpen(false)}
              />
            )
          }

          {
            openCsvModal && (
              <DownloadCsvModal
                isOpen={openCsvModal}
                project={projectCsv!}
                toggle={() => setOpenCsvModal(false)}
              />
            )
          }
        </>
      )}

      <DialogAlert
        handleDelete={handleDeleteProjectConfirmed}
        dialogOpen={dialogOpen}
        handleClose={handleClose}
        dialogTitle={t('delete-option-project')}
        dialogMessage={t('delete-verification-project', {projectRowName})}
      />
    </>
  );
}
